import { useId, FC, ReactElement } from 'react';

import type { RadioGroupItemProps } from '@radix-ui/react-radio-group';
import reactToText from 'react-to-text';

import { childrenRecipe, labelStyle, radioButtonRadioStyle } from './RadioButton.css';
import { hasComponent } from '../../../util/children';
import { Image as SparkyImage } from '../../Image/Image';
import { Stack } from '../../Stack/Stack';
import { RadioBase } from '../RadioBase';
import { useRadioGroup } from '../RadioGroupContext';
import { RadioIndicator } from '../RadioIndicator';

type ImageProps = Pick<
  Parameters<typeof SparkyImage>[0],
  'alt' | 'height' | 'maxHeight' | 'maxWidth' | 'src' | 'width'
>;

const Image: FC<ImageProps> = ({ ...props }) => <SparkyImage {...props} objectFit="contain"></SparkyImage>;

type RadioButtonProps = Pick<RadioGroupItemProps, 'value'> & {
  alignLabel?: 'center' | 'start';
  children: ReactElement<typeof Image> | string;
};

type CompoundRadioButtonProps = {
  Image: typeof Image;
};

export const RadioButton: FC<RadioButtonProps> & CompoundRadioButtonProps = ({
  alignLabel = 'start',
  children,
  value,
}) => {
  const id = useId();
  const { ariaDescribedby } = useRadioGroup();

  const hasImage = hasComponent(children, Image);

  return (
    <label className={labelStyle} data-label={reactToText(children)} htmlFor={id}>
      <Stack gap="2" direction="row" alignY={hasImage ? 'center' : 'start'}>
        <Stack.Item shrink={false}>
          <RadioBase
            className={radioButtonRadioStyle}
            aria-label={reactToText(children)}
            aria-describedby={ariaDescribedby}
            id={id}
            value={value}>
            <RadioIndicator />
          </RadioBase>
        </Stack.Item>
        <div className={childrenRecipe({ alignLabel })}>{children}</div>
      </Stack>
    </label>
  );
};

RadioButton.Image = Image;
RadioButton.Image.displayName = 'RadioButton.Image';
